import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import useUrlTab from "@/src/hooks/useUrlTab";

import { getProjectOfferOptions } from "./offerAnalytics.utils";
import OfferAnalyticsFilters from "./OfferAnalyticsFilters";
import SubscriptionsTab from "./subscriptions/SubscriptionsTab";
import UnsubscriptionsTab from "./unsubscriptions/UnsubscriptionsTab";
import useOfferAnalyticsFilters from "./useOfferAnalyticsFilters";
import NavTabs from "../../common/NavTabs";
import PageTitle from "../../common/PageTitle";
import InfoPanel from "../../utils/InfoPanel";
import AnalyticsPage from "../AnalyticsPage";

import type { JSX } from "react";

import type { AppState } from "@/src/reducers";

import type { OfferAnalyticsTab } from "./offerAnalytics.types";
import type { NavTabItem } from "../../common/NavTabs";

const TAB_SUBSCRIPTIONS = "subscriptions";
const TAB_UNSUBSCRIPTIONS = "unsubscriptions";
const AVAILABLE_TAB_LIST = [
  TAB_SUBSCRIPTIONS,
  TAB_UNSUBSCRIPTIONS,
] satisfies OfferAnalyticsTab[];

const INFO_PANEL_LINKS = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000101760-analytics-sales-subscriptions-and-unsubscriptions-churn-",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000101760-rapports-d-analyse-ventes-abonnements-et-d%C3%A9sabonnements-churn-",
};

function OfferAnalytics(): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const offers = useSelector((state: AppState) => state.fetchReducer.offers);
  const projectUuid = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );

  const offerOptionList = useMemo(
    () => getProjectOfferOptions(offers, projectUuid),
    [offers, projectUuid],
  );
  const knownOfferUuidList = useMemo(
    () =>
      offers.isFetched
        ? offerOptionList.map((offerOption) => offerOption.value)
        : undefined,
    [offers.isFetched, offerOptionList],
  );

  const { activeTab, setActiveTab } = useUrlTab({
    availableTabs: AVAILABLE_TAB_LIST,
    defaultTab: TAB_SUBSCRIPTIONS,
  });
  const { filters, setPeriod, setOfferUuid } =
    useOfferAnalyticsFilters(knownOfferUuidList);

  const tabList: readonly NavTabItem[] = [
    {
      value: TAB_SUBSCRIPTIONS,
      label: t("offerAnalytics.tabs.subscriptions"),
      content: <SubscriptionsTab filters={filters} />,
    },
    {
      value: TAB_UNSUBSCRIPTIONS,
      label: t("offerAnalytics.tabs.unsubscriptions"),
      content: <UnsubscriptionsTab filters={filters} />,
    },
  ];

  return (
    <AnalyticsPage
      breadcrumbList={[{ name: t("editor-menu:analytics") }]}
      heading={
        <PageTitle>
          {t("editor-menu:offer_analytics")}
          <InfoPanel links={INFO_PANEL_LINKS} />
        </PageTitle>
      }
    >
      <NavTabs
        onValueChange={setActiveTab}
        tabList={tabList}
        value={activeTab}
        actions={
          <OfferAnalyticsFilters
            filters={filters}
            offerOptionList={offerOptionList}
            onOfferChange={setOfferUuid}
            onPeriodChange={setPeriod}
          />
        }
      />
    </AnalyticsPage>
  );
}

export default OfferAnalytics;
